<template>

    <v-modal :title="$t('titles.review')" size="lg" :loading="starting" :error="startingError">

        <template #subtitle><v-uuid v-if="review" :identifier="review.id" :uuid="review.uuid" /></template>

        <template #pagination v-if="reviews.length">
            <v-button-icon icon="fas fa-arrow-up-right-from-square" width="40px" height="40px" @click.native="viewReview(null)" />
            <v-button-icon icon="fas fa-chevron-up" width="40px" height="40px" @click.native="prevReview" :disabled="review && reviews.findIndex(r => r.id === review.id) === 0" />
            <v-button-icon icon="fas fa-chevron-down" width="40px" height="40px" @click.native="nextReview" :disabled="review && reviews.findIndex(r => r.id === review.id) >= reviews.length - 1" />
        </template>

        <template #body>
            <div class="row mb--15">
                <div class="col col-12 col-lg-7 mb-15">
                    <v-modal-section :title="$t('titles.review')">
                        <template #body>
                            <v-button-toolbar class="w-fluid align-items-end" size="md">
                                <v-form-group class="flex-grow-1" :label="$t('labels.rating')">
                                    <v-select-rating :model="params.score" @input="params.score = $event" />
                                </v-form-group>
                                <v-form-group v-if="hasSentiment">
                                    <v-sentiment-scale :score="review.sentiment_score" outlined />
                                </v-form-group>
                            </v-button-toolbar>
                            <v-form-group :label="$t('labels.review')">
                                <v-quill id="review" :model="params.review" @input="params.review = $event" :height="400" mode="highlight" disabled />
                            </v-form-group>
                            <v-related-reviews v-if="review" :review-id="review.id" :show-similarity="false" />
                        </template>
                        <template #footer>
                            <v-button size="xs" :loading="loading === 'save'" @click.native="updateReview">{{ $t('labels.save') }}</v-button>
                        </template>
                    </v-modal-section>
                    <v-modal-section v-if="review.form" :title="$t('titles.custom_fields')" :collapsed="!review.fields.length">
                        <template v-if="!review.fields.length" #body>
                            <v-notice v-if="!review.fields.length" size="sm" icon="fas fa-align-left" :title="$t('titles.no_custom_fields')">
                                <v-button size="sm" theme="white" @click.native="viewReview('#custom-fields')">{{ $t('labels.add_custom_field') }}</v-button>
                            </v-notice>
                        </template>
                        <template v-else #body>
                            <v-form-group v-for="field in review.fields" :key="field.uuid" :label="field.field_label">
                                <v-uploader v-if="field.field_type === 'image'"
                                    :uploader-id="`uploader-${field.uuid}`"
                                    :uploader-width="'100%'"
                                    :uploader-height="300"
                                    :uploader-image="field.image?.link"
                                    :uploader-url="`/reviews/fields/${field.id}/image`">
                                </v-uploader>
                                <v-input v-else-if="field.field_type === 'text'" :model="field.answer" @input="updateAnswer(field.id, $event)" />
                                <v-input v-else-if="field.field_type === 'url'" :model="field.answer" @input="updateAnswer(field.id, $event)" />
                                <v-textarea v-else-if="field.field_type === 'paragraph'" :model="field.answer" @input="updateAnswer(field.id, $event)" />
                                <v-select v-else-if="field.field_type === 'checkbox'" :model="field.answer" @input="updateAnswer(field.id, $event)">
                                    <option value="">{{ $t('labels.select_an_option') }}</option>
                                    <option :value="true">{{ $t('labels.yes') }}</option>
                                    <option :value="false">{{ $t('labels.no') }}</option>
                                </v-select>
                                <v-select v-else-if="field.field_type === 'select'" :model="field.answer" @input="updateAnswer(field.id, $event)">
                                    <option value="">{{ $t('labels.select_an_option') }}</option>
                                    <option v-if="field.answer && !(field.options || []).includes(field.answer)" :value="field.answer">{{ field.answer }}</option>
                                    <option v-for="option in (field.options || [])" :key="option" :value="option">{{ option }}</option>
                                </v-select>
                            </v-form-group>
                        </template>
                        <template v-if="review.fields.length" #footer>
                            <v-button size="xs" theme="white" @click.native="viewReview('#custom-fields')">{{ $t('labels.add') }}</v-button>
                            <v-button size="xs" :loading="loading === 'fields-update'" @click.native="updateFields">{{ $t('labels.save') }}</v-button>
                        </template>
                    </v-modal-section>
                    <v-modal-section id="reply" :title="$t('titles.reply')">
                        <template #body>
                            <v-form-group v-if="review.integration?.has_replies" :caption="review.replied_at | dateTime()">
                                <v-textarea :rows="10" :placeholder="`${$t('labels.replying_on')} ${review.integration.name}...`" :model="params.reply" @input="params.reply = $event" />
                                <v-generate-reply v-if="!review.reply" class="mt-10" :review-id="review.id" @change="params.reply = $event" />
                            </v-form-group>
                            <v-form-group v-else :label="$t('labels.status')" :caption="$t('captions.review_reply_status')">
                                <v-select class="w-50p" :model="params.replied" @input="params.replied = $event">
                                    <option :value="false">{{ $t('labels.unreplied') }}</option>
                                    <option :value="true">{{ $t('labels.replied') }}</option>
                                </v-select>
                            </v-form-group>
                        </template>
                        <template #footer>
                            <v-button size="xs" :loading="loading === 'reply-update'" @click.native="updateReply">{{ $t('labels.save') }}</v-button>
                        </template>
                    </v-modal-section>
                </div>
                <div class="col col-12 col-lg-5 mb-15">
                    <v-modal-section v-if="hasActions">
                        <div class="actions d-flex" :style="{ 'gap': '10px' }">
                            <v-button-icon icon="fas fa-eye" active-icon="fas fa-eye-slash" width="100%" height="40px" :active="Boolean(review.is_hidden)" :loading="loading === 'flag-hidden'" @click.native="flagHidden(review.is_hidden ? 0 : 1)" v-tooltip="{ 'content': $t('labels.hide_from_public') }" />
                            <v-button-icon icon="fas fa-clone" width="100%" height="40px" :active="Boolean(review.is_duplicate)" :loading="loading === 'flag-duplicate'" @click.native="flagDuplicate(review.is_duplicate ? 0 : 1)" v-tooltip="{ 'content': $t('labels.flag_as_duplicate') }" />
                            <v-button-icon icon="fas fa-reply" width="100%" height="40px" @click.native="scrollToReply" v-tooltip="{ 'content': $t('labels.reply') }" />
                            <v-button-icon icon="fas fa-image" width="100%" height="40px" @click.native="$store.commit('setModal', 'review-share-' + review.id)" v-tooltip="{ 'content': $t('labels.share') }" />
                            <v-button-icon icon="fas fa-trash" width="100%" height="40px" @click.native="$store.commit('setModal', 'review-delete-' + review.id)" v-tooltip="{ 'content': $t('labels.delete') }" />
                        </div>
                    </v-modal-section>
                    <v-modal-section v-if="customer" :title="$t('titles.reviewer')">
                        <v-customer-card :customer="customer" />
                    </v-modal-section>
                    <v-modal-section v-else-if="reviewer" :title="$t('titles.reviewer')">
                        <template #body>
                            <v-form-group :label="$t('labels.first_name')">
                                <v-input :model="params.first_name" @input="params.first_name = $event" />
                            </v-form-group>
                            <v-form-group :label="$t('labels.last_name')">
                                <v-input :model="params.last_name" @input="params.last_name = $event" />
                            </v-form-group>
                            <v-form-group :label="$t('labels.company')">
                                <v-input :model="params.company" @input="params.company = $event" />
                            </v-form-group>
                        </template>
                        <template #footer>
                            <v-button size="xs" :loading="loading === 'reviewer-update'" @click.native="updateReviewer">{{ $t('labels.save') }}</v-button>
                        </template>
                    </v-modal-section>
                    <v-modal-section :title="$t('titles.details')">
                        <template #body>
                            <v-form-group :label="$t('labels.date')">
                                <v-date-picker :model="params.created_at" @input="params.created_at = $event" />
                            </v-form-group>
                            <v-form-group :label="$t('labels.source')">
                                <v-select-source :model="params.source_id" @input="params.source_id = $event" />
                            </v-form-group>
                            <v-form-group v-if="project.locations.length" :label="$t('labels.location')">
                                <v-select-location :model="params.location_id" @input="params.location_id = $event" />
                            </v-form-group>
                            <v-form-group :label="$t('labels.tags')">
                                <v-multiselect-tags context="review" :model="params.tag_ids" @input="params.tag_ids = $event" />
                            </v-form-group>
                            <v-form-group :label="$t('labels.external_link')">
                                <v-input placeholder="https://" :model="params.external_url" @input="params.external_url = $event" />
                            </v-form-group>
                        </template>
                        <template #footer>
                            <v-button size="xs" :loading="loading === 'details-update'" @click.native="updateDetails">{{ $t('labels.save') }}</v-button>
                        </template>
                    </v-modal-section>
                    <v-modal-section v-if="review.ask || review.template || review.form" :title="$t('titles.meta')">
                        <v-table condensed>
                            <tbody>
                                <tr v-if="review.ask">
                                    <td class="w-160">{{ $t('labels.requested') }}</td>
                                    <td>{{ review.ask.scheduled_at | relativeTime }}</td>
                                </tr>
                                <tr v-if="review.template">
                                    <td class="w-160">{{ $t('labels.template') }}</td>
                                    <td><router-link :to="{ name: `settings.${review.template.channel}`, query: { 'slug': review.template.slug } }">{{ review.template.name | truncate(20) }}</router-link></td>
                                </tr>
                                <tr v-if="review.template">
                                    <td class="w-160">{{ $t('labels.channel') }}</td>
                                    <td>{{ $t(`labels.${review.template.channel}`) }}</td>
                                </tr>
                                <tr v-if="review.form">
                                    <td class="w-160">{{ $t('labels.review_page') }}</td>
                                    <td><router-link :to="{ name: 'forms.show', params: { id: review.form.id } }">{{ review.form.name | truncate(20) }}</router-link></td>
                                </tr>
                            </tbody>
                        </v-table>
                    </v-modal-section>
                </div>
            </div>
        </template>

        <template #footer>
            <v-button size="sm" theme="white" @click.native="cancel">{{ $t('labels.cancel') }}</v-button>
            <div></div>
        </template>

    </v-modal>

</template>

<script>

import ReviewApi from '@api/ReviewApi';
import CustomerCard from '@cmp/CustomerCard';
import GenerateReply from '@cmp/GenerateReply';
import Modal from '@cmp/Modal';
import ModalSection from '@cmp/ModalSection';
import RelatedReviews from '@cmp/RelatedReviews';
import SentimentScale from '@cmp/SentimentScale';
import UUID from '@cmp/UUID';
import DatePicker from '@forms/DatePicker';
import MultiselectTags from '@forms/MultiselectTags';
import Quill from '@forms/Quill';
import SelectLocation from '@forms/SelectLocation';
import SelectRating from '@forms/SelectRating';
import SelectSource from '@forms/SelectSource';
import Uploader from '@forms/Uploader';

export default {

    components: {
        'v-customer-card': CustomerCard,
        'v-date-picker': DatePicker,
        'v-generate-reply': GenerateReply,
        'v-modal': Modal,
        'v-modal-section': ModalSection,
        'v-multiselect-tags': MultiselectTags,
        'v-quill': Quill,
        'v-related-reviews': RelatedReviews,
        'v-select-location': SelectLocation,
        'v-select-rating': SelectRating,
        'v-select-source': SelectSource,
        'v-sentiment-scale': SentimentScale,
        'v-uploader': Uploader,
        'v-uuid': UUID,
    },

    props: {
        focusReply: {
            type: Boolean,
            default: false,
        },
        hasActions: {
            type: Boolean,
            default: true,
        },
        reviewId: {
            type: Number,
            required: true,
        },
        reviews: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            pendingScrollToReply: false,
            starting: true,
            startingError: null,
            loading: false,
            review: null,
            params: {
                fields: [],
                reviewer: {},
                tag_ids: [],
                reply: null,
                replied: false,
            },
        }
    },

    computed: {

        customer() {
            return this.review ? this.review.customer : null;
        },

        hasSentiment() {
            const score = this.review?.sentiment_score;
            return score !== null && score !== undefined && !Number.isNaN(Number(score));
        },

        reviewer() {
            return this.review ? this.review.reviewer : null;
        },

    },

    mounted() {
        this.pendingScrollToReply = this.focusReply;
        this.getReview();
    },

    watch: {

        review(val) {
            this.params = {
                first_name: val.reviewer?.first_name,
                last_name: val.reviewer?.last_name,
                company: val.reviewer?.company,
                score: val.score,
                review: val.review_html,
                created_at: val.created_at,
                source_id: val.source ? val.source.id : null,
                external_url: val.external_url,
                location_id: val.location ? val.location.id : null,
                tag_ids: val.tags.map(tag => tag.id),
                reply: val.reply,
                replied: Boolean(val.replied_at),
                fields: val.fields.map(field => ({ id: field.id, answer: field.answer })),
            };
        },

    },

    methods: {

        getReview(id = null) {
            this.starting = true;
            ReviewApi.getReview(id ?? this.reviewId)
                .then((response) => {
                    this.review = response.data.data;
                    this.starting = false;
                    if (this.pendingScrollToReply) {
                        this.pendingScrollToReply = false;
                        this.$nextTick(() => this.scrollToReply());
                    }
                })
                .catch((error) => {
                    this.startingError = error.response.data.message;
                    this.starting = false;
                });
        },

        nextReview() {
            const index = this.reviews.findIndex(r => r.id === this.review.id);
            const review = index >= 0 && index + 1 < this.reviews.length ? this.reviews[index + 1] : this.reviews[0];
            if (review) this.getReview(review.id);
        },

        prevReview() {
            const index = this.reviews.findIndex(r => r.id === this.review.id);
            const review = index > 0 ? this.reviews[index - 1] : this.reviews[0];
            if (review) this.getReview(review.id);
        },

        updateReview() {
            this.loading = 'save';
            ReviewApi.updateReview(this.review.id, this.params)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        updateDetails() {
            this.loading = 'details-update';
            ReviewApi.updateDetails(this.review.id, this.params)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        updateFields() {
            this.loading = 'fields-update';
            ReviewApi.updateFields(this.review.id, this.params)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                }, (error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        updateReviewer() {
            this.loading = 'reviewer-update';
            ReviewApi.updateReviewer(this.review.id, this.params)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        updateReply() {
            this.loading = 'reply-update';
            let params = {
                reply: this.params.reply,
                replied: this.params.replied,
            }
            ReviewApi.updateReply(this.review.id, params)
                .then((response) => {
                    this.loading = false;
                    this.review = response.data.data;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        deleteReply() {
            this.loading = 'reply-delete';
            ReviewApi.deleteReply(this.review.id)
                .then((response) => {
                    this.loading = false;
                    this.review = response.data.data;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        flagHidden(isHidden) {
            this.loading = 'flag-hidden';
            let params = { is_hidden: isHidden }
            ReviewApi.flagHidden(this.review.id, params)
                .then((response) => {
                    this.loading = false;
                    this.review = response.data.data;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        flagDuplicate(isDuplicate) {
            this.loading = 'flag-duplicate';
            let params = { is_duplicate: isDuplicate }
            ReviewApi.flagDuplicate(this.review.id, params)
                .then((response) => {
                    this.loading = false;
                    this.review = response.data.data;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        viewCustomer() {
            if (!this.review?.customer?.id) {
                return;
            }
            this.$router.push({
                name: 'customers.show',
                params: { id: this.review.customer.id },
            }).catch(() => {});
        },

        viewReview(hash = null) {
            if (!hash) {
                this.$router.push({
                    name: 'reviews.show',
                    params: { id: this.review.id },
                }).catch(() => {});
            } else {
                this.$router.push({
                    name: 'reviews.show',
                    params: { id: this.review.id },
                    hash: hash,
                }).catch(() => {});
            }
        },

        updateAnswer(id, value) {
            this.params.fields.find(f => f.id === id).answer = value;
        },

        scrollToReply() {
            const el = document.querySelector('#reply');
            el.scrollIntoView({ behavior: 'smooth' });
        },

        cancel() {
            this.$store.commit('setModal', null);
        },

    },

}

</script>
